import { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { colors, strCode } from "../../../consts";
import { metricsConfig } from "./consts/metricsConfig";
import { userStatisticsUtils } from "./utils/userStatisticsUtils";

import { FlexContainers, LineChart, SVG, WrapperContainer } from "../../../components";
import { DetailedReportModal } from "./components/DetailedReportModal/DetailedReportModal";
import { LineChartTooltip } from "./components/LineChartTooltip/LineChartTooltip";
import { StatsInfo } from "./templates/StatsInfo/StatsInfo";
import { UserStatisticsCardTemplate } from "./templates/UserStatisticsCardTemplate/UserStatisticsCardTemplate";

import { LineChartContainer } from "./styled";

const UserStatistics = ({ userInsightsData, lastSegmentedDate, loading }) => {
  const [detailedModalActive, setDetailedModalActive] = useState(false);

  const {
    totalUsers,
    totalConnected,
    totalNotConnected,
    totalConnectedPercent,
    totalNotConnectedPercent,
    conversionStats,
    chartData,
    notConnectedStats
  } = useMemo(() => {
    return userStatisticsUtils.getStatistics(userInsightsData);
  }, [userInsightsData]);

  return (
    <>
      <WrapperContainer Container={FlexContainers.FlexRow} width={"100%"} gap={16}>
        <UserStatisticsCardTemplate
          title={strCode.TOTAL_USERS_TITLE}
          icon={<SVG.TotalUsersIcon />}
          iconBackground={colors.BLUE}
          stats={totalUsers}
          loading={loading}>
          <WrapperContainer Container={FlexContainers.FlexColumn} width={"100%"} gap={2}>
            <StatsInfo
              title={strCode.CONVERSION_TITLE}
              stats={conversionStats.conversion}
              loading={loading}
            />
            <StatsInfo
              title={strCode.NON_CONVERSION_TITLE}
              stats={conversionStats.nonConversion}
              loading={loading}
            />
          </WrapperContainer>
        </UserStatisticsCardTemplate>

        <UserStatisticsCardTemplate
          title={strCode.CONNECTED_TITLE}
          icon={<SVG.CheckMark size={18} />}
          iconBackground={colors.SUCCESS}
          onExpand={() => {
            setDetailedModalActive(true);
          }}
          stats={
            <>
              {totalConnectedPercent}% <span>({totalConnected})</span>
            </>
          }
          loading={loading}>
          <LineChartContainer Container={FlexContainers.FlexColumn} width={"100%"} gap={2}>
            <LineChart
              key={"segmentation"}
              data={chartData}
              metricsConfig={metricsConfig}
              title={strCode.SEGMENTATION_TITLE}
              TooltipComponent={<LineChartTooltip data={chartData} total={totalConnected} />}
            />
          </LineChartContainer>
        </UserStatisticsCardTemplate>

        <UserStatisticsCardTemplate
          title={strCode.NOT_CONNECTED_TITLE}
          icon={<SVG.CrossMark size={18} />}
          iconBackground={colors.RAISIN_50}
          stats={
            <>
              {totalNotConnectedPercent}% <span>({totalNotConnected})</span>
            </>
          }
          loading={loading}>
          <WrapperContainer Container={FlexContainers.FlexColumn} width={"100%"} gap={2}>
            <StatsInfo
              title={strCode.TRIED_BUT_FAILED_SEGMENT_TITLE}
              stats={notConnectedStats.triedButFailed}
              loading={loading}
            />
            <StatsInfo
              title={strCode.NEVER_TRIED_SEGMENT_TITLE}
              stats={notConnectedStats.neverTried}
              loading={loading}
            />
          </WrapperContainer>
        </UserStatisticsCardTemplate>
      </WrapperContainer>

      <DetailedReportModal
        data={chartData}
        lastSegmentedDate={lastSegmentedDate}
        active={detailedModalActive}
        setActive={setDetailedModalActive}
      />
    </>
  );
};

UserStatistics.propTypes = {
  userInsightsData: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      createdAtTs: PropTypes.number.isRequired,
      segment: PropTypes.string,
      conversionType: PropTypes.bool
    })
  ).isRequired,
  lastSegmentedDate: PropTypes.string,
  loading: PropTypes.bool.isRequired
};
export default UserStatistics;
